import { lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BookOpen, LogOut, User, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';

import { SEOHead } from '@/components/SEOHead';

// Lazy-load non-critical landing components
const StatsHeaderBadge = lazy(() => import('@/components/StatsHeaderBadge').then(m => ({ default: m.StatsHeaderBadge })));
const HeroSection = lazy(() => import('@/components/landing/HeroSection').then(m => ({ default: m.HeroSection })));
const FeaturesCarousel3D = lazy(() => import('@/components/landing/FeaturesCarousel3D').then(m => ({ default: m.FeaturesCarousel3D })));
const AboutSection = lazy(() => import('@/components/landing/AboutSection').then(m => ({ default: m.AboutSection })));
const SpacedRepetitionSection = lazy(() => import('@/components/landing/SpacedRepetitionSection').then(m => ({ default: m.SpacedRepetitionSection })));
const LandingFooter = lazy(() => import('@/components/landing/LandingFooter').then(m => ({ default: m.LandingFooter })));

// Preload functions for common routes
const preloadAuth = () => import('./Auth');
const preloadDashboard = () => import('./Index');
const preloadLists = () => import('./Lists');
const preloadLearn = () => import('./Learn');

export default function Landing() {
  const { t } = useLanguage();
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Navigation items for logged-in users
  const navItems = [
    { path: '/dashboard', label: t.nav.home },
    { path: '/lists', label: t.nav.books },
    { path: '/learn', label: t.nav.learn },
  ];

  return (
    <>
      <SEOHead path="/" />
      <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="container flex h-14 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 font-serif font-semibold text-foreground">
              <BookOpen className="h-5 w-5 text-primary" />
              <span>GermanPath</span>
            </Link>
            
            {/* Desktop navigation for logged-in users */}
            {user && (
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    onMouseEnter={
                      item.path === '/dashboard' ? preloadDashboard :
                      item.path === '/lists' ? preloadLists :
                      item.path === '/learn' ? preloadLearn : undefined
                    }
                  >
                    <Button variant="ghost" size="sm">
                      {item.label}
                    </Button>
                  </Link>
                ))}
              </nav>
            )}
          </div>
          
          <div className="flex items-center gap-1">
            {/* Theme toggle */}
            <ThemeToggle />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline max-w-[120px] truncate">
                      {user.email}
                    </span>
                    {isAdmin && (
                      <Badge variant="secondary" className="text-xs">
                        {t.nav.admin}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover">
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    {user.email}
                  </div>
                  {isAdmin && (
                    <div className="px-2 py-1">
                      <Badge variant="secondary" className="text-xs">
                        {t.nav.admin}
                      </Badge>
                    </div>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <Settings className="h-4 w-4 mr-2" />
                    {t.nav.profile}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t.nav.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" onMouseEnter={preloadAuth}>
                <Button size="sm">{t.nav.signIn}</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
        <Suspense fallback={<div className="min-h-[40vh]" />}>
          <HeroSection />
        </Suspense>
        <Suspense fallback={null}>
          <FeaturesCarousel3D />
        </Suspense>
        <Suspense fallback={null}>
          <AboutSection />
        </Suspense>
        <Suspense fallback={null}>
          <SpacedRepetitionSection />
        </Suspense>
      </main>
      
      <Suspense fallback={null}>
        <LandingFooter />
      </Suspense>
    </div>
    </>
  );
}
